// src/components/weeklyReport/ReportsGrid.js
//
// Submitted patient-level weekly reports as a grid, 9 per page with Prev/Next.
// A card shows who/when and the counts; clicking it opens the full report
// (studies, people, follow-ups, pre-screens, and each study's decision).
// Used by Admin Reports → Submitted reports and by the weekly report page's
// past reports. What it is given is already limited by Firestore rules.

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ClipboardList } from "lucide-react";
import { loadReportApprovals } from "../../services/studyReportApprovalService";
import { ReportChips, ReportDetails, reportByline } from "./ReportPatientsList";
import { BTN_SMALL, WrModal, periodText } from "./ui";

export const PAGE_SIZE = 9;

const ReportsGrid = ({ reports = [], label = "Reports", showCoordinator = true, emptyText = "No reports yet." }) => {
  const [page, setPage] = useState(1);
  const [approvals, setApprovals] = useState({});
  const [openId, setOpenId] = useState(null);

  // A new list (filter, time period, another coordinator) starts at page 1.
  useEffect(() => {
    setPage(1);
  }, [reports]);

  const pageCount = Math.max(1, Math.ceil(reports.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = reports.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Study-number decisions for the reports on this page (fail-soft).
  const pageIds = pageItems.map((r) => r.id).join(",");
  useEffect(() => {
    const ids = pageIds ? pageIds.split(",").filter((id) => !(id in approvals)) : [];
    if (ids.length === 0) return undefined;
    let cancelled = false;
    loadReportApprovals(ids)
      .then((more) => !cancelled && setApprovals((prev) => ({ ...prev, ...more })))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIds]);

  const openReport = reports.find((r) => r.id === openId) || null;

  if (reports.length === 0) return <p className="text-sm text-slate-400 text-center py-4">{emptyText}</p>;

  return (
    <div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5" aria-label={label}>
        {pageItems.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => setOpenId(r.id)}
              className="weekly-report-row w-full h-full text-left rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] p-3 transition"
            >
              <div className="text-sm font-semibold text-white truncate">
                {showCoordinator ? r.coordinator || "Unknown" : periodText(r)}
              </div>
              {showCoordinator && <div className="text-[11px] text-slate-300">{periodText(r)}</div>}
              <div className="mt-0.5 text-[10px] text-slate-400 break-words">{reportByline(r)}</div>
              <div className="mt-2 flex flex-wrap gap-1">
                <ReportChips report={r} />
              </div>
            </button>
          </li>
        ))}
      </ul>

      {pageCount > 1 && (
        <nav className="mt-3 flex items-center justify-center gap-2" aria-label={`${label} pages`}>
          <button
            type="button"
            className={BTN_SMALL}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            aria-label="Previous page"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Prev
          </button>
          <span className="text-[11px] text-slate-400">
            Page {currentPage} of {pageCount}
          </span>
          <button
            type="button"
            className={BTN_SMALL}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={currentPage >= pageCount}
            aria-label="Next page"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </nav>
      )}

      <WrModal
        open={!!openReport}
        onClose={() => setOpenId(null)}
        wide
        icon={ClipboardList}
        title={openReport ? `${openReport.coordinator || "Unknown"} · ${periodText(openReport)}` : ""}
        subtitle={openReport ? reportByline(openReport) : ""}
      >
        {openReport && <ReportDetails report={openReport} approval={approvals[openReport.id]} />}
      </WrModal>
    </div>
  );
};

export default ReportsGrid;
